import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import { Empty } from "antd";
import { ArrowRightOutlined, FileImageOutlined, FileTextOutlined } from "@ant-design/icons";

import { GalleryDraftBar } from "../component/gallery";
import { GalleryOrgan, useGalleryOrgans } from "../hooks/useGalleryOrgans";
import { ACCESS_TOKEN } from "../lib/cookie";
import { MainColor, SubColor } from "../style/styleValue";

const STRIP_H = 150; // 썸네일 스트립 높이(px)

const GalleryDraftShelf = () => {
  const navigate = useNavigate();
  const { organs, loading, error } = useGalleryOrgans();

  const [activeId, setActiveId] = useState<number | null>(null);
  const rowRefs = useRef<Map<number, HTMLElement>>(new Map());

  useEffect(() => {
    if (!localStorage.getItem(ACCESS_TOKEN)) navigate("/login");
  }, [navigate]);

  // 스크롤 위치에 맞춰 좌측 인덱스의 현재 항목을 표시
  useEffect(() => {
    if (organs.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length === 0) return;
        const top = visible.reduce((a, b) => (a.boundingClientRect.top < b.boundingClientRect.top ? a : b));
        const id = Number((top.target as HTMLElement).dataset.organId);
        if (!Number.isNaN(id)) setActiveId(id);
      },
      { rootMargin: "-80px 0px -60% 0px" }
    );

    rowRefs.current.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [organs]);

  const scrollToOrgan = (id: number) => {
    rowRefs.current.get(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <Page>
      <GalleryDraftBar active="shelf" />

      <Head>
        <Eyebrow>ATLAS GALLERY</Eyebrow>
        <Title>Digital Slide Archive</Title>
      </Head>

      {error ? (
        <CenterState>
          <Empty description="갤러리 목록을 불러오지 못했습니다." />
        </CenterState>
      ) : loading ? (
        <CenterState>
          <SkeletonStack>
            {Array.from({ length: 4 }).map((_, i) => (
              <SkeletonRow key={i} />
            ))}
          </SkeletonStack>
        </CenterState>
      ) : organs.length === 0 ? (
        <CenterState>
          <Empty description="표시할 organ 이 없습니다." />
        </CenterState>
      ) : (
        <Body>
          <IndexCol>
            <IndexLabel>CHAPTER ({organs.length})</IndexLabel>
            <IndexList>
              {organs.map((organ, i) => (
                <IndexItem
                  key={organ.id}
                  $active={organ.id === activeId}
                  onClick={() => scrollToOrgan(organ.id)}
                  title={organ.name}
                >
                  <IndexNum>{String(i + 1).padStart(2, "0")}</IndexNum>
                  <IndexName>{organ.name}</IndexName>
                  <IndexCnt>{organ.slideCnt}</IndexCnt>
                </IndexItem>
              ))}
            </IndexList>
          </IndexCol>

          <Rows>
            {organs.map((organ) => (
              <ShelfRow
                key={organ.id}
                organ={organ}
                onOpen={() => navigate(`/post-list/${organ.id}`)}
                registerRef={(el) => {
                  if (el) rowRefs.current.set(organ.id, el);
                  else rowRefs.current.delete(organ.id);
                }}
              />
            ))}
          </Rows>
        </Body>
      )}
    </Page>
  );
};

interface RowProps {
  organ: GalleryOrgan;
  onOpen: () => void;
  registerRef: (el: HTMLElement | null) => void;
}

const ShelfRow = ({ organ, onOpen, registerRef }: RowProps) => (
  <Row ref={registerRef} data-organ-id={organ.id}>
    <RowHead onClick={onOpen}>
      <RowTitleBox>
        <RowTitle>{organ.name}</RowTitle>
        <RowCounts>
          <span>
            <FileTextOutlined /> {organ.postCnt}
          </span>
          <span>
            <FileImageOutlined /> {organ.slideCnt}
          </span>
        </RowCounts>
      </RowTitleBox>
      <OpenLink>
        전체 보기 <ArrowRightOutlined />
      </OpenLink>
    </RowHead>

    {organ.subNames.length > 0 && (
      <RowSubs title={organ.subNames.join(", ")}>{organ.subNames.join(" · ")}</RowSubs>
    )}

    <Strip onClick={onOpen}>
      {organ.thumbs.length === 0 ? (
        organ.thumbsLoading ? (
          Array.from({ length: 5 }).map((_, i) => <StripSkeleton key={i} />)
        ) : (
          <EmptyStrip>등록된 이미지가 없습니다.</EmptyStrip>
        )
      ) : (
        organ.thumbs.map((url, i) => (
          <StripTile key={url}>
            <img
              src={url}
              alt={`${organ.name} ${i + 1}`}
              loading="lazy"
              onError={(e) => {
                const el = e.currentTarget;
                el.style.display = "none";
                (el.parentElement as HTMLElement).classList.add("broken");
              }}
            />
          </StripTile>
        ))
      )}
    </Strip>
  </Row>
);

const shimmer = keyframes`
  0% { background-position: -320px 0; }
  100% { background-position: 320px 0; }
`;

const Page = styled.div`
  width: 100%;
  padding-bottom: 5rem;
  background: #fff;
`;

const Head = styled.div`
  padding: 3rem 4rem 1.75rem;
  border-bottom: 1px solid #eef1f5;

  @media (max-width: 768px) {
    padding: 2rem 1.25rem 1.25rem;
  }
`;

const Eyebrow = styled.span`
  display: block;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.2em;
  color: ${SubColor};
  margin-bottom: 10px;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 2.3rem;
  font-weight: 700;
  color: ${MainColor};
  letter-spacing: -0.5px;
`;

const Sub = styled.p`
  margin: 10px 0 0;
  font-size: 0.9rem;
  color: #8a94a3;
`;

const CenterState = styled.div`
  min-height: 40vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 4rem;
`;

const Body = styled.div`
  display: grid;
  grid-template-columns: 220px 1fr;
  gap: 40px;
  padding: 2rem 4rem 0;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 0;
    padding: 1.5rem 1.25rem 0;
  }
`;

const IndexCol = styled.aside`
  position: sticky;
  top: 70px;
  align-self: start;
  max-height: calc(100vh - 110px);
  overflow-y: auto;

  @media (max-width: 1024px) {
    display: none;
  }
`;

const IndexLabel = styled.div`
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  color: #a8b1bd;
  padding-bottom: 10px;
  border-bottom: 1px solid #eef1f5;
  margin-bottom: 8px;
`;

const IndexList = styled.nav`
  display: flex;
  flex-direction: column;
`;

const IndexItem = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  border: none;
  background: ${({ $active }) => ($active ? "#f2f6fb" : "transparent")};
  cursor: pointer;
  text-align: left;
  padding: 7px 10px;
  border-radius: 8px;
  color: ${({ $active }) => ($active ? MainColor : "#667085")};
  font-weight: ${({ $active }) => ($active ? 700 : 500)};

  &:hover {
    background: #f7f9fb;
    color: ${MainColor};
  }
`;

const IndexNum = styled.span`
  font-size: 0.66rem;
  color: #b9c2ce;
  flex-shrink: 0;
`;

const IndexName = styled.span`
  flex: 1;
  min-width: 0;
  font-size: 0.85rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const IndexCnt = styled.span`
  font-size: 0.7rem;
  color: #b9c2ce;
  flex-shrink: 0;
`;

const Rows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 44px;
  min-width: 0;
`;

const Row = styled.section`
  scroll-margin-top: 80px;
  min-width: 0;
`;

const RowHead = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  cursor: pointer;
`;

const RowTitleBox = styled.div`
  display: flex;
  align-items: baseline;
  gap: 14px;
  min-width: 0;
`;

const RowTitle = styled.h2`
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a2433;
  letter-spacing: -0.3px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  ${RowHead}:hover & {
    color: ${MainColor};
  }
`;

const RowCounts = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
  font-size: 0.78rem;
  color: #8a94a3;

  span {
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }
`;

const OpenLink = styled.span`
  flex-shrink: 0;
  font-size: 0.8rem;
  font-weight: 600;
  color: ${SubColor};
  display: inline-flex;
  align-items: center;
  gap: 5px;
  opacity: 0.7;
  transition: opacity 0.2s ease, transform 0.2s ease;

  ${RowHead}:hover & {
    opacity: 1;
    transform: translateX(2px);
  }
`;

const RowSubs = styled.p`
  margin: 6px 0 0;
  font-size: 0.78rem;
  color: #a8b1bd;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Strip = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 14px;
  padding-bottom: 8px;
  overflow-x: auto;
  cursor: pointer;
  scrollbar-width: thin;

  &::-webkit-scrollbar {
    height: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: #e1e6ed;
    border-radius: 3px;
  }
  &:hover::-webkit-scrollbar-thumb {
    background: #cbd4df;
  }
`;

const StripTile = styled.div`
  position: relative;
  flex: 0 0 auto;
  width: ${Math.round(STRIP_H * 1.34)}px;
  height: ${STRIP_H}px;
  overflow: hidden;
  border-radius: 10px;
  background: #f0f2f5;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.4s ease;
  }

  &:hover img {
    transform: scale(1.06);
  }

  &.broken::after {
    content: "";
    position: absolute;
    inset: 0;
    background: repeating-linear-gradient(45deg, #f0f2f5, #f0f2f5 10px, #e8ebf0 10px, #e8ebf0 20px);
  }
`;

const StripSkeleton = styled.div`
  flex: 0 0 auto;
  width: ${Math.round(STRIP_H * 1.34)}px;
  height: ${STRIP_H}px;
  border-radius: 10px;
  background: linear-gradient(90deg, #eef1f5 25%, #f7f9fb 50%, #eef1f5 75%);
  background-size: 640px 100%;
  animation: ${shimmer} 1.3s linear infinite;
`;

const EmptyStrip = styled.div`
  display: flex;
  align-items: center;
  height: ${STRIP_H}px;
  padding: 0 20px;
  border-radius: 10px;
  background: #f7f9fb;
  color: #b9c2ce;
  font-size: 0.85rem;
`;

const SkeletonStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 28px;
  width: 100%;
`;

const SkeletonRow = styled.div`
  height: ${STRIP_H + 40}px;
  border-radius: 12px;
  background: linear-gradient(90deg, #eef1f5 25%, #f7f9fb 50%, #eef1f5 75%);
  background-size: 640px 100%;
  animation: ${shimmer} 1.3s linear infinite;
`;

export default GalleryDraftShelf;
